/**
 * CoevaluationsUserActivity Component
 *
 * Features:
 * - Displays and manages the peer coevaluation process for a user's activity.
 * - Handles PDF modal view, comment, and coevaluation item selection.
 * - Sends coevaluation data to backend and updates parent state.
 * - Uses Redux for academy info and i18next for translations.
 * - Uses CSS modules for styling and Material-UI for UI.
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { saveUserCoevaluationHomework } from 'store/lesson-actions';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';

import Coevaluation from 'components/UI/Coevaluation/Coevaluation';
import ModalViewPDF from 'components/UI/ModalViewPDF/ModalViewPDF';

import classes from './CoevaluationUserActivity.module.css';

const CoevaluationsUserActivity = props => {
  const { t } = useTranslation(['lesson']);
  const dataAcademy = useSelector(state => state.academyInfo);
  // State to store selected coevaluation items
  const [coevaluationActivityData, setCoevaluationActivityData] =
    useState([]);
  // State to control the PDF modal visibility
  const [openActivity, setOpenActivity] = useState(false);
  // State to store the coevaluation comment
  const [coevaluationComment, setCoevaluationComment] = useState('');

  const openActivityHandler = () => {
    setOpenActivity(true);
  };

  const closeActivityHandler = () => {
    setOpenActivity(false);
  };

  const coevaluationCommentHandler = comment => {
    setCoevaluationComment(comment);
  };

  const coevaluationActivityHandler = (orderItem, valueItem) => {
    let addNewData = {
      orderItem,
      valueItem
    };
    let data = coevaluationActivityData;
    data = data.filter(item => item.orderItem !== orderItem);
    data.push(addNewData);
    setCoevaluationActivityData(data);
  };

  const sendCoevaluationHandler = () => {
    let coevalTotal = 0;
    coevaluationActivityData.map(item => {
      coevalTotal += item.valueItem;
    });
    (async () => {
      const coevalActivity = await saveUserCoevaluationHomework(
        props.idUserActivity,
        props.idCourse,
        props.idLesson,
        coevalTotal,
        dataAcademy.data.shortName,
        coevaluationComment
      );
      if (coevalActivity) {
        props.closeCoevalUser();
        props.activitiesCoevaluate(props.idUserActivity);
        props.changeCoevaluationStatus();
      }
    })();
  };

  return (
    <>
      <p className={classes.title}>{props.title}</p>
      <p className={classes.instructions}>
        {t('peer_evaluate_instructions')}
      </p>
      <Button
        onClick={openActivityHandler}
        className={classes.nextBtn}
      >
        {t('see_activity')}
      </Button>
      <Button
        onClick={props.closeCoevalUser}
        className={classes.nextBtn}
      >
        {t('back')}
      </Button>
      {/* Props passed to Coevaluation:
          - coevaluationItems: props.coevaluationsItems (array)
          - coevaluationActivity: coevaluationActivityHandler (function)
          - coevaluationCommentHandler: coevaluationCommentHandler (function)
      */}
      <Coevaluation
        coevaluationItems={props.coevaluationsItems}
        coevaluationActivity={coevaluationActivityHandler}
        coevaluationCommentHandler={coevaluationCommentHandler}
      />
      <Button
        disabled={
          !(
            coevaluationActivityData.length ===
            props.coevaluationsItems.length
          )
        }
        onClick={sendCoevaluationHandler}
        className={classes.sendCoevaluation}
      >
        {t('send_peer_review')}
      </Button>
      {/* Props passed to ModalViewPDF:
          - urlCloudFront: props.pdf
          - openModal: openActivity
          - onCloseModal: closeActivityHandler
          - urlViewPDF: props.pdf
          - titlePDF: props.title
      */}
      <ModalViewPDF
        urlCloudFront={props.pdf}
        openModal={openActivity}
        onCloseModal={closeActivityHandler}
        urlViewPDF={props.pdf}
        titlePDF={props.title}
      />
    </>
  );
};

CoevaluationsUserActivity.propTypes = {
  /**
   * PDF URL for the activity
   */
  pdf: PropTypes.string,
  /**
   * Title of the activity
   */
  title: PropTypes.string,
  /**
   * Function to close the coevaluation user modal
   */
  closeCoevalUser: PropTypes.func,
  /**
   * Array of coevaluation items
   */
  coevaluationsItems: PropTypes.array,
  /**
   * Course ID
   */
  idCourse: PropTypes.number,
  /**
   * Lesson ID
   */
  idLesson: PropTypes.number,
  /**
   * User activity ID
   */
  idUserActivity: PropTypes.number,
  /**
   * Function to update activities coevaluate
   */
  activitiesCoevaluate: PropTypes.func,
  /**
   * Function to change coevaluation status
   */
  changeCoevaluationStatus: PropTypes.func
};

CoevaluationsUserActivity.defaultProps = {
  pdf: '',
  title: '',
  closeCoevalUser: () => {},
  coevaluationsItems: [],
  idCourse: null,
  idLesson: null,
  idUserActivity: null,
  activitiesCoevaluate: () => {},
  changeCoevaluationStatus: () => {}
};

export default CoevaluationsUserActivity;
